<template>
  <wz-card class="mb-3">
    <!-- Header: título + botón subir (esquina superior derecha), mismo patrón que "Documentos de cese". -->
    <div class="flex justify-between items-center mb-3">
      <h5 class="mb-0 font-bold">Adendas</h5>
      <wz-button variant="outline" size="sm" @click="openUploadModal">
        <i class="ri-upload-2-line mr-1"></i>Subir adenda
      </wz-button>
    </div>

    <!-- Estado vacío -->
    <div v-if="!loading && !addenda.length" class="text-center py-4 text-muted">
      <i class="ri-file-list-3-line d-block" style="font-size: 1.8rem;"></i>
      <p class="mt-2 mb-3">Este contrato no tiene adendas aún.</p>
      <wz-button variant="outline" size="sm" @click="openUploadModal">
        <i class="ri-upload-2-line mr-1"></i>Subir adenda
      </wz-button>
    </div>

    <!-- Lista de adendas -->
    <div v-else class="addenda-list">
      <div
        v-for="a in addenda"
        :key="a.id"
        :id="'addendum-' + a.id"
        class="addendum-row flex items-center justify-between"
        :class="{ 'addendum-row--highlight': highlightedId === a.id }"
      >
        <div class="flex items-start" style="gap: 12px; min-width: 0;">
          <i class="ri-file-pdf-2-line addendum-icon"></i>
          <div style="min-width: 0;">
            <div class="addendum-name">{{ a.fileName }}</div>
            <div v-if="a.description" class="addendum-desc">{{ a.description }}</div>
            <div v-if="a.changedFields && a.changedFields.length" class="addendum-desc">
              Campos: {{ a.changedFields.map(fieldLabel).join(', ') }}
              <span v-if="a.status === 'PENDIENTE'" class="addendum-status-pending">· Pendiente</span>
            </div>
            <div class="addendum-meta">
              {{ formatUploadedAt(a.uploadedAt) }} · {{ readableSize(a.fileSizeBytes) }}
            </div>
          </div>
        </div>
        <div class="flex items-center" style="gap: 8px; flex-shrink: 0;">
          <wz-button variant="outline" size="sm" @click="downloadAddendum(a)" :disabled="downloadingId === a.id">
            <i class="ri-download-2-line mr-1"></i>Descargar
          </wz-button>
          <wz-button variant="outline-danger" size="sm" @click="confirmDelete(a)">
            <i class="ri-delete-bin-line"></i>
          </wz-button>
        </div>
      </div>
    </div>

    <!-- Historial de sueldo/cargo del contrato -->
    <div v-if="salaryHistory.length" class="salary-history mt-4">
      <h6 class="font-bold mb-2">Historial de sueldo y cargo</h6>
      <table class="w-full salary-history-table">
        <thead>
          <tr>
            <th>Fecha efectiva</th><th>Sueldo (S/)</th><th>Cargo</th><th>Origen</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="h in salaryHistory" :key="h.id">
            <td>{{ formatDate(h.effectiveFrom) }}</td>
            <td>{{ h.baseSalary }}</td>
            <td>{{ h.positionName || '—' }}</td>
            <td>{{ h.addendumId ? 'Adenda' : 'Alta del contrato' }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Modal de subida -->
    <wz-modal v-model="showUploadModal" title="Subir adenda" @close="resetForm">
      <div class="mb-3">
        <label>Archivo * <small class="text-muted">(PDF — máx. 100MB)</small></label>
        <wz-file-input
          :key="fileKey"
          class="w-full mt-2"
          id-file="contract-addendum-upload"
          :accept-file="['.pdf']"
          @onChange="onFileChange"
        />
      </div>

      <div class="mb-3">
        <AddendaFieldsForm ref="fieldsForm" :key="fieldsKey" v-model="addendaFields" :company-id="companyId" optional />
      </div>

      <div v-if="hasChanges" class="mb-3">
        <wz-datepicker label="Fecha efectiva *" v-model="effectiveDate" :max="maxEffectiveDate" />
        <small v-if="hasNonDateAware" class="text-muted d-block mt-1">
          Los cambios de área, canal, línea de gasto, sede, jefe directo o responsable solo pueden aplicarse a partir de hoy.
        </small>
      </div>

      <div class="mb-3">
        <label>Descripción <small class="text-muted">(opcional)</small></label>
        <textarea
          v-model="description"
          class="w-full mt-2 addendum-textarea"
          rows="3"
          maxlength="500"
          placeholder="Ej.: Aumento de sueldo, cambio de área…"
        ></textarea>
        <small class="text-muted d-block text-right">{{ description.length }}/500</small>
      </div>

      <div class="flex justify-end mt-3">
        <wz-button variant="light" class="mr-2" @click="showUploadModal = false">Cancelar</wz-button>
        <wz-button variant="primary" @click="upload" :disabled="uploading">
          {{ uploading ? 'Subiendo...' : 'Subir' }}
        </wz-button>
      </div>
    </wz-modal>

    <!-- Confirmación de borrado -->
    <wz-confirm-modal
      v-model="deleteModal"
      title="Eliminar adenda"
      :description="deleteTarget ? `¿Eliminar esta adenda? Esta acción no se puede deshacer.` : ''"
      accept-text="Eliminar"
      accept-variant="danger"
      @confirm="onDeleteConfirmed"
    />
  </wz-card>
</template>

<script>
import { Alert } from '@wizepe/wizeui'
import ContractAddendumService from '@/core/services/ContractAddendumService'
import AddendaFieldsForm, { NON_DATE_AWARE_FIELDS } from './AddendaFieldsForm.vue'
import { downloadErrorMessage, fileSizeReadable } from '@/core/utils'
import fileDownload from 'js-file-download'

const MAX_SIZE_BYTES = 100 * 1024 * 1024 // 100MB

export default {
  name: 'ContractAddendaSection',
  components: { AddendaFieldsForm },
  props: {
    contractId: { type: [Number, String], required: true },
    companyId: { type: [Number, String], default: null },
    // Adenda a resaltar al abrir el detalle desde el listado (scroll + flash de 2s).
    highlightAddendumId: { type: [Number, String], default: null },
  },
  data() {
    return {
      addenda: [],
      highlightedId: null,
      highlightTimer: null,
      salaryHistory: [],
      loading: false,
      // Subida
      showUploadModal: false,
      uploading: false,
      file: null,
      fileKey: 0,
      description: '',
      // Adenda multi-campo (DEF-004: delegado a AddendaFieldsForm vía v-model).
      addendaFields: { changedFields: [] },
      fieldsKey: 0,
      effectiveDate: null,
      // Descarga / borrado
      downloadingId: null,
      deleteModal: false,
      deleteTarget: null,
    }
  },
  computed: {
    fieldOptions() {
      return [
        { key: 'BASE_SALARY', label: 'Sueldo' },
        { key: 'POSITION', label: 'Cargo' },
        { key: 'AREA', label: 'Área' },
        { key: 'CHANNEL', label: 'Canal' },
        { key: 'EXPENSE_LINE', label: 'Línea de gasto' },
        { key: 'WORK_SITE', label: 'Sede' },
        { key: 'DIRECT_MANAGER', label: 'Jefe directo' },
        { key: 'HR_RESPONSIBLE_EMAIL', label: 'Responsable' },
      ]
    },
    changedFields() {
      return this.addendaFields.changedFields || []
    },
    hasChanges() {
      return this.changedFields.length > 0
    },
    hasNonDateAware() {
      return this.changedFields.some(f => NON_DATE_AWARE_FIELDS.includes(f))
    },
    maxEffectiveDate() {
      // Si hay campos no date-aware, la fecha efectiva no puede ser futura (max = hoy).
      return this.hasNonDateAware ? new Date().toISOString().slice(0, 10) : null
    },
  },
  methods: {
    formatDate(value) {
      if (!value) return '—'
      // effective_from es fecha (YYYY-MM-DD): formatear por componentes para evitar el corrimiento de
      // zona horaria (new Date('YYYY-MM-DD') se parsea en UTC y en UTC-5 muestra el día anterior).
      const s = String(value).slice(0, 10)
      const parts = s.split('-')
      if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`
      const d = new Date(value)
      return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('es-PE')
    },
    readableSize(bytes) {
      return fileSizeReadable(bytes)
    },
    async load() {
      try {
        this.loading = true
        const [addenda, history] = await Promise.all([
          ContractAddendumService.list(this.contractId),
          ContractAddendumService.salaryHistory(this.contractId),
        ])
        this.addenda = addenda.payload || []
        this.salaryHistory = history.payload || []
        this.maybeHighlight()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al cargar las adendas')
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    // Si venimos del listado con una adenda objetivo, hacer scroll a su fila y resaltarla 2s.
    maybeHighlight() {
      const target = this.highlightAddendumId != null && this.highlightAddendumId !== ''
        ? Number(this.highlightAddendumId)
        : null
      if (!target) return
      this.$nextTick(() => {
        const el = this.$el.querySelector('#addendum-' + target)
        if (!el) return
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        this.highlightedId = target
        if (this.highlightTimer) clearTimeout(this.highlightTimer)
        this.highlightTimer = setTimeout(() => {
          if (this.highlightedId === target) this.highlightedId = null
        }, 2000)
      })
    },
    openUploadModal() {
      this.resetForm()
      this.showUploadModal = true
    },
    resetForm() {
      this.file = null
      this.description = ''
      this.addendaFields = { changedFields: [] }
      this.fieldsKey += 1 // re-monta AddendaFieldsForm para limpiar su estado
      this.effectiveDate = null
      this.fileKey += 1 // re-monta el wz-file-input para limpiar el archivo
    },
    fieldLabel(key) {
      const opt = this.fieldOptions.find(o => o.key === key)
      return opt ? opt.label : key
    },
    onFileChange(files) {
      this.file = files && files.length > 0 ? files[0] : null
    },
    isPdf(file) {
      const byType = file.type === 'application/pdf'
      const byName = (file.name || '').toLowerCase().endsWith('.pdf')
      return byType || byName
    },
    async upload() {
      if (!this.file) {
        Alert.error('Selecciona un archivo PDF')
        return
      }
      if (!this.isPdf(this.file)) {
        Alert.error('El archivo debe ser un PDF')
        return
      }
      if (this.file.size > MAX_SIZE_BYTES) {
        Alert.error('La adenda no debe superar los 100MB')
        return
      }
      // Validaciones multi-campo (consistentes con el backend).
      if (this.hasChanges && !this.effectiveDate) {
        Alert.error('La fecha efectiva es obligatoria cuando la adenda cambia campos')
        return
      }
      const fieldError = this.$refs.fieldsForm && this.$refs.fieldsForm.validate()
      if (fieldError) { Alert.error(fieldError); return }
      if (this.hasNonDateAware && this.effectiveDate && this.effectiveDate > new Date().toISOString().slice(0, 10)) {
        Alert.error('Los cambios de área/canal/línea/sede/jefe/responsable no admiten fecha futura')
        return
      }
      try {
        this.uploading = true
        const f = this.addendaFields
        await ContractAddendumService.upload(this.contractId, this.file, this.description.trim() || null, {
          changedFields: f.changedFields,
          newBaseSalary: f.newBaseSalary,
          newPositionId: f.newPositionId,
          newAreaId: f.newAreaId,
          newChannelId: f.newChannelId,
          newExpenseLineId: f.newExpenseLineId,
          newWorkSiteId: f.newWorkSiteId,
          newDirectManagerId: f.newDirectManagerId,
          newHrResponsibleEmail: f.newHrResponsibleEmail,
          effectiveDate: this.hasChanges ? this.effectiveDate : null,
        })
        Alert.success('Adenda subida correctamente')
        this.showUploadModal = false
        await this.load()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al subir la adenda')
        console.error(e)
      } finally {
        this.uploading = false
      }
    },
    async downloadAddendum(a) {
      try {
        this.downloadingId = a.id
        const response = await ContractAddendumService.download(this.contractId, a.id)
        fileDownload(response.data, a.fileName || 'adenda.pdf')
      } catch (e) {
        Alert.error(await downloadErrorMessage(e) || 'Error al descargar la adenda')
        console.error(e)
      } finally {
        this.downloadingId = null
      }
    },
    confirmDelete(a) {
      this.deleteTarget = a
      this.deleteModal = true
    },
    async onDeleteConfirmed() {
      const target = this.deleteTarget
      if (!target) return
      try {
        await ContractAddendumService.delete(this.contractId, target.id)
        Alert.success('Adenda eliminada')
        await this.load()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al eliminar la adenda')
        console.error(e)
      } finally {
        this.deleteTarget = null
      }
    },
    // Fecha legible: "hoy" / "ayer" / "hace N días" para lo reciente; fecha local para lo más viejo.
    formatUploadedAt(value) {
      if (!value) return '—'
      const d = new Date(value)
      if (isNaN(d.getTime())) return '—'
      const today = new Date()
      const dayMs = 86400000
      const diffDays = Math.floor((today.setHours(0, 0, 0, 0) - new Date(value).setHours(0, 0, 0, 0)) / dayMs)
      if (diffDays <= 0) return 'hoy'
      if (diffDays === 1) return 'ayer'
      if (diffDays < 7) return `hace ${diffDays} días`
      return d.toLocaleDateString('es-PE')
    },
  },
  watch: {
    // Clic en "Ver" de otra adenda del MISMO contrato (ya cargado): re-resaltar sin recargar.
    highlightAddendumId() {
      this.maybeHighlight()
    },
  },
  created() {
    this.load()
  },
  beforeDestroy() {
    if (this.highlightTimer) clearTimeout(this.highlightTimer)
  },
}
</script>

<style lang="stylus" scoped>
.addenda-list
  display flex
  flex-direction column
  gap 8px

.addendum-row
  padding 10px 12px
  border 1px solid var(--admin-border-color)
  border-radius 6px
  scroll-margin-top 80px

// Flash de 2s al llegar desde el listado (?addendaId=): se desvanece a transparente.
.addendum-row--highlight
  animation addendum-flash 2s ease-out

@keyframes addendum-flash
  0%
    background-color rgba(37, 99, 235, 0.20)
    border-color #2563eb
  100%
    background-color transparent
    border-color var(--admin-border-color)

.addendum-icon
  font-size 1.6rem
  color #dc2626
  line-height 1

.addendum-name
  font-weight 600
  word-break break-word

.addendum-desc
  font-size 13px
  color var(--admin-text-muted)
  margin-top 2px

.addendum-status-pending
  color #b45309
  font-weight 600

.addendum-meta
  font-size 12px
  color var(--admin-text-muted)
  margin-top 2px

.addendum-textarea
  box-sizing border-box
  padding 8px
  border 1px solid var(--admin-border-color)
  border-radius 6px
  resize vertical
  font-family inherit
  font-size 14px

.salary-history-table
  border-collapse collapse
  font-size 13px

  th, td
    border 1px solid var(--admin-border-color)
    padding 6px 10px
    text-align left

  th
    background var(--admin-bg-muted, #f8f9fa)
    font-weight 600
</style>
